import React from 'react';

export type Variant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'body1'
  | 'body1-medium'
  | 'body1-bold'
  | 'body2'
  | 'body2-medium'
  | 'body2-bold'
  | 'body3'
  | 'body3-medium'
  | 'body3-bold';

type TextProps<T extends React.ElementType> = {
  as?: T;
  variant?: Variant;
  className?: string;
  children: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<T>, 'as'>;

/* ------------------------ Variant Styles ------------------------ */
const variantStyles: Record<Variant, React.CSSProperties> = {
  h1: {
    fontFamily: 'Inter',
    fontSize: 'var(--h1)',
    fontWeight: 600,
    lineHeight: 1.2,
    color: 'var(--text-primary)'
  },
  h2: {
    fontFamily: 'Inter',
    fontSize: 'var(--h2)',
    fontWeight: 600,
    lineHeight: 1.25,
    color: 'var(--text-primary)'
  },
  h3: {
    fontFamily: 'Inter',
    fontSize: 'var(--h3)',
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'var(--text-primary)'
  },

  body1: {
    fontFamily: 'Inter',
    fontSize: 'var(--body1)',
    fontWeight: 400,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  },
  'body1-medium': {
    fontFamily: 'Inter',
    fontSize: 'var(--body1-medium)',
    fontWeight: 500,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  },
  'body1-bold': {
    fontFamily: 'Inter',
    fontSize: 'var(--body1-bold)',
    fontWeight: 600,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  },

  body2: {
    fontFamily: 'Inter',
    fontSize: 'var(--body2)',
    fontWeight: 400,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  },
  'body2-medium': {
    fontFamily: 'Inter',
    fontSize: 'var(--body2-medium)',
    fontWeight: 500,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  },
  'body2-bold': {
    fontFamily: 'Inter',
    fontSize: 'var(--body2-bold)',
    fontWeight: 600,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  },

  body3: {
    fontFamily: 'Inter',
    fontSize: 'var(--body3)',
    fontWeight: 400,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  },
  'body3-medium': {
    fontFamily: 'Inter',
    fontSize: 'var(--body3-medium)',
    fontWeight: 500,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  },
  'body3-bold': {
    fontFamily: 'Inter',
    fontSize: 'var(--body3-bold)',
    fontWeight: 600,
    lineHeight: 1.6,
    color: 'var(--text-primary)'
  }
};

/* Default HTML element mapping */
const defaultElement: Record<Variant, React.ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  body1: 'span',
  'body1-medium': 'span',
  'body1-bold': 'span',
  body2: 'span',
  'body2-medium': 'span',
  'body2-bold': 'span',
  body3: 'span',
  'body3-medium': 'span',
  'body3-bold': 'span'
};

export function Text<T extends React.ElementType = 'p'>({
  as,
  variant = 'body1',
  className,
  children,
  style: customStyle,
  ...props
}: TextProps<T>) {
  const Component = as || defaultElement[variant];
  const variantStyle = variantStyles[variant];

  // Merge variant styles with custom styles
  const mergedStyle = { ...variantStyle, ...customStyle };

  return (
    <Component style={mergedStyle} className={className} {...props}>
      {children}
    </Component>
  );
}
